<script>
import AssignmentControl from './AssignmentControl.vue';
import TicketPriorityBadge from '../../shared/TicketPriorityBadge.vue';

/**
 * Ticket board card: id+priority row, title+email, assignee + received date.
 * Archive state = archived_at (not a status replacement).
 * Merged tickets stay in the closed column group; Unito badge is visual only.
 */
export default {
    name: 'TicketCard',
    components: { AssignmentControl, TicketPriorityBadge },
    props: {
        ticket: {
            type: Object,
            required: true,
            // Expected keys: id, title, customer_email, status, status_label,
            // priority, priority_label, channel_name, category_name,
            // assigned_user_id, assigned_user_name, assigned_user_initials,
            // created_at, created_at_label, customer_rating_*, archived_at,
            // permissions, routes; optional merged
        },
        showAssignment: { type: Boolean, default: false },
        highlighted: { type: Boolean, default: false },
        /** Visual archive flag; also true when ticket.archived_at is set */
        archived: { type: Boolean, default: false },
        assignees: { type: Array, default: () => [] },
        currentUserId: { type: [Number, String], default: null },
    },
    emits: ['assign', 'archive', 'restore', 'open'],
    computed: {
        permissions() {
            return this.ticket.permissions || {};
        },
        routes() {
            return this.ticket.routes || {};
        },
        isArchived() {
            return this.archived || Boolean(this.ticket.archived_at);
        },
        isClosedGroup() {
            const status = String(this.ticket.status || '').toLowerCase();
            return status === 'closed' || status === 'merged';
        },
        isMerged() {
            if (this.ticket.merged === true) {
                return true;
            }
            return String(this.ticket.status || '').toLowerCase() === 'merged';
        },
        priorityTone() {
            const raw = String(this.ticket.priority_label || this.ticket.priority || '').toLowerCase();
            if (raw.includes('alt') || raw === 'high' || raw === '1' || raw === 'urgent') {
                return 'high';
            }
            if (raw.includes('med') || raw === 'medium' || raw === '2' || raw === 'normal') {
                return 'medium';
            }
            return 'low';
        },
        showCustomerRating() {
            const tier = this.ticket.customer_rating_tier;
            const label = this.ticket.customer_rating_tier_label;
            if (!tier || !label) {
                return false;
            }
            const known = ['top', 'basic', 'dangerous'];
            return known.includes(String(tier).toLowerCase());
        },
        customerRatingBadgeClass() {
            const variant = this.ticket.customer_rating_badge;
            if (variant === 'success' || variant === 'danger' || variant === 'warning' || variant === 'neutral') {
                return `tickets-index-badge--rating-${variant}`;
            }
            return 'tickets-index-badge--rating-neutral';
        },
        showArchiveControls() {
            return this.isClosedGroup && this.permissions.can_archive === true;
        },
        showAssignControl() {
            return this.showAssignment
                && !this.isArchived
                && this.permissions.can_assign === true;
        },
        assigneeName() {
            return this.ticket.assigned_user_name || null;
        },
        assigneeInitials() {
            const raw = this.ticket.assigned_user_initials;
            if (typeof raw === 'string' && raw.trim() !== '') {
                return raw.trim();
            }
            return null;
        },
        createdAtLabel() {
            return this.ticket.created_at_label || null;
        },
        customerEmail() {
            const email = this.ticket.customer_email;
            return typeof email === 'string' && email.trim() !== '' ? email.trim() : null;
        },
        categoryName() {
            const name = this.ticket.category_name;
            return typeof name === 'string' && name.trim() !== '' ? name.trim() : null;
        },
        channelName() {
            const name = this.ticket.channel_name;
            return typeof name === 'string' && name.trim() !== '' ? name.trim() : null;
        },
        showUrl() {
            return this.routes.show || null;
        },
        cardClasses() {
            return {
                'tickets-index-card--highlighted': this.highlighted,
                'tickets-index-card--archived': this.isArchived,
                [`tickets-index-card--priority-${this.priorityTone}`]: true,
            };
        },
    },
    methods: {
        onAssign(userId) {
            this.$emit('assign', { ticketId: this.ticket.id, userId });
        },
        onArchiveToggle() {
            if (this.isArchived) {
                this.$emit('restore', this.ticket.id);
                return;
            }
            this.$emit('archive', this.ticket.id);
        },
        onOpen() {
            this.$emit('open', this.ticket);
        },
    },
};
</script>

<template>
    <article
        class="tickets-index-card"
        :class="cardClasses"
        :aria-label="'Ticket ' + ticket.id + ': ' + (ticket.title || '')"
    >
        <!-- Row 1: #id + tags | priority (Jira symbols) -->
        <div class="tickets-index-card__row tickets-index-card__row--id">
            <div class="tickets-index-card__row-start">
                <a
                    v-if="showUrl"
                    class="tickets-index-card__code"
                    :href="showUrl"
                    @click="onOpen"
                >#{{ ticket.id }}</a>
                <span
                    v-else
                    class="tickets-index-card__code"
                >#{{ ticket.id }}</span>
                <span
                    v-if="isMerged"
                    class="tickets-index-badge tickets-index-badge--sm"
                >Unito</span>
                <span
                    v-if="categoryName"
                    class="tickets-index-badge tickets-index-badge--category"
                    data-testid="ticket-board-category"
                >{{ categoryName }}</span>
                <span
                    v-if="channelName"
                    class="tickets-index-badge tickets-index-badge--sm tickets-index-badge--secondary"
                >{{ channelName }}</span>
                <span
                    v-if="showCustomerRating"
                    class="tickets-index-badge tickets-index-badge--sm"
                    :class="customerRatingBadgeClass"
                    data-testid="ticket-board-customer-rating"
                >{{ ticket.customer_rating_tier_label }}</span>
            </div>
            <TicketPriorityBadge
                :priority="ticket.priority"
                :priority-label="ticket.priority_label"
                test-id="ticket-board-priority"
            />
        </div>

        <!-- Row 2: title | email -->
        <div class="tickets-index-card__row tickets-index-card__row--title">
            <h3 class="tickets-index-card__title">
                <a
                    v-if="showUrl"
                    :href="showUrl"
                    @click="onOpen"
                >{{ ticket.title }}</a>
                <template v-else>{{ ticket.title }}</template>
            </h3>
            <span
                v-if="customerEmail"
                class="tickets-index-card__email"
                :title="customerEmail"
            >{{ customerEmail }}</span>
        </div>

        <!-- Row 3: assignee | Data ricezione -->
        <div class="tickets-index-card__row tickets-index-card__row--meta">
            <div class="tickets-index-card__assignee">
                <span
                    v-if="assigneeInitials"
                    class="tickets-index-avatar"
                    :title="assigneeName || assigneeInitials"
                    data-testid="ticket-board-assignee-avatar"
                >{{ assigneeInitials }}</span>
                <span
                    v-else
                    class="tickets-index-avatar tickets-index-avatar--empty"
                    title="Non assegnato"
                    aria-label="Non assegnato"
                >—</span>
                <span class="tickets-index-card__assignee-label">
                    {{ assigneeName || 'Non assegnato' }}
                </span>
            </div>
            <div
                v-if="createdAtLabel"
                class="tickets-index-card__received"
            >
                <span class="tickets-index-card__received-label">Data ricezione</span>
                <time
                    class="tickets-index-card__date"
                    :datetime="ticket.created_at || undefined"
                >{{ createdAtLabel }}</time>
            </div>
        </div>

        <div
            v-if="showArchiveControls"
            class="tickets-index-card__archive-row"
        >
            <button
                type="button"
                class="tickets-index-btn tickets-index-btn--ghost tickets-index-btn--sm"
                @click="onArchiveToggle"
            >
                <span aria-hidden="true">{{ isArchived ? '↺' : '▣' }}</span>
                {{ isArchived ? 'Ripristina' : 'Archivia' }}
            </button>
        </div>

        <AssignmentControl
            v-if="showAssignControl"
            :assigned-user-id="ticket.assigned_user_id"
            :assignees="assignees"
            :current-user-id="currentUserId"
            @assign="onAssign"
        />
    </article>
</template>
